import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Check, ArrowRight } from 'lucide-react';
import '../styles/Pricing.css';

const Pricing = () => {
  const pricingModels = [
    {
      name: 'Fixed Project',
      description: 'Best for well-defined projects with clear scope',
      features: [
        'Fixed timeline & budget',
        'Detailed project specification',
        'Milestone-based payments',
        'Complete project ownership',
        'Post-launch support (30 days)'
      ],
      ideal: 'MVP development, Website redesign, Mobile app launch',
      starting: 'Starting at ₹8,00,000'
    },
    {
      name: 'Time & Materials',
      description: 'Flexible engagement for evolving requirements',
      features: [
        'Hourly or daily rates',
        'Flexible scope changes',
        'Transparent time tracking',
        'Pay only for work done',
        'Scale team up or down'
      ],
      ideal: 'Ongoing development, Feature additions, Maintenance',
      starting: '₹4,000-8,000/hour'
    },
    {
      name: 'Monthly Retainer',
      description: 'Dedicated team for continuous development',
      features: [
        'Dedicated developers',
        'Priority support',
        'Predictable monthly cost',
        'Long-term partnership',
        'Strategic planning included'
      ],
      ideal: 'Product scaling, Continuous improvements, SEO & Growth',
      starting: 'Starting at ₹4,00,000/month'
    }
  ];

  const typicalProjects = [
    {
      type: 'MVP Mobile App',
      timeline: '8-12 weeks',
      budget: '₹12,00,000 - ₹28,00,000',
      includes: 'iOS/Android app, Backend API, Admin panel, App store deployment'
    },
    {
      type: 'Business Website',
      timeline: '4-6 weeks',
      budget: '₹6,00,000 - ₹14,00,000',
      includes: 'Responsive design, CMS integration, SEO setup, Contact forms'
    },
    {
      type: 'Web Application',
      timeline: '10-16 weeks',
      budget: '₹20,00,000 - ₹48,00,000',
      includes: 'Full-stack development, Database design, Authentication, Third-party integrations'
    },
    {
      type: 'SEO & Growth (Monthly)',
      timeline: 'Ongoing',
      budget: '₹1,60,000 - ₹4,00,000/month',
      includes: 'Technical SEO, Content creation, Link building, Analytics reporting'
    }
  ];

  return (
    <div className="pricing-page" data-testid="pricing-page">
      <Navbar />
      
      <section className="pricing-hero">
        <div className="container">
          <div className="hero-content fade-in-up">
            <h1 className="page-title" data-testid="pricing-title">Pricing & Engagement Models</h1>
            <p className="page-subtitle" data-testid="pricing-subtitle">
              Flexible engagement models to fit your budget and project needs
            </p>
          </div>
        </div>
      </section>

      <section className="pricing-models">
        <div className="container">
          <div className="pricing-grid">
            {pricingModels.map((model, index) => (
              <div className="pricing-card card" key={index} data-testid={`pricing-model-${index}`}>
                <h3>{model.name}</h3>
                <p className="model-description">{model.description}</p>
                <div className="model-price">{model.starting}</div>
                
                <div className="model-features">
                  <h4>What's Included:</h4>
                  <ul>
                    {model.features.map((feature, i) => (
                      <li key={i}>
                        <Check size={18} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="model-ideal">
                  <strong>Ideal for:</strong> {model.ideal}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="typical-projects">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Typical Project Costs</h2>
            <p className="section-subtitle">Ballpark estimates for common project types</p>
          </div>

          <div className="projects-grid">
            {typicalProjects.map((project, index) => (
              <div className="project-estimate card" key={index} data-testid={`project-estimate-${index}`}>
                <h3>{project.type}</h3>
                <div className="estimate-details">
                  <div className="estimate-item">
                    <span className="estimate-label">Timeline:</span>
                    <span className="estimate-value">{project.timeline}</span>
                  </div>
                  <div className="estimate-item">
                    <span className="estimate-label">Budget:</span>
                    <span className="estimate-value budget-value">{project.budget}</span>
                  </div>
                </div>
                <div className="estimate-includes">
                  <strong>Includes:</strong> {project.includes}
                </div>
              </div>
            ))}
          </div>

          <div className="pricing-note">
            <p>
              <strong>Note:</strong> Actual costs vary based on project complexity, features, and technical requirements. 
              Contact us for a detailed quote tailored to your specific needs.
            </p>
          </div>
        </div>
      </section>

      <section className="pricing-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to get started?</h2>
            <p>Let's discuss your project and provide a detailed quote.</p>
            <Link to="/contact" className="btn btn-primary btn-large" data-testid="pricing-contact-cta">
              Request a Quote
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Pricing;